import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { ReportsTable } from "../../../../components/dashboard/service/reports/reports-table";
import { getReportsAsync } from "../../../../slices/service/reports";
import {
  reportStatusesOptions,
  reportViewsOptions,
} from "../../../../components/dashboard/service/reports/utils";
import { useSearchParams } from "react-router-dom";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { BuildingLibraryIcon, PlusIcon } from "@heroicons/react/24/outline";
import ServiceReportEditModal from "../../../../components/dashboard/service/reports/service-report-edit-modal";
import {
  axiosWithToast,
  pageOptions,
  updateUrlWithException,
} from "../../../../components/Utils";
import { ReportActionEditBar } from "../../../../components/dashboard/service/reports/report-action-edit-bar";
import ReportMultiEditDialog from "../../../../components/dashboard/service/reports/report-multi-edit-dialog";
import { debounce } from "lodash";
import { fetchReportExcel } from "../../../../api/service/report";
import { ReportExportDialog } from "../../../../components/dashboard/service/reports/reports-export-dialog";
import ReportOverviewSummary from "../../../../components/dashboard/service/reports/ReportOverviewSummary";

export const ReportList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL or default
  const getInitialFiltersFromUrl = () => {
    const filtersParam = searchParams.get("filters");
    if (filtersParam) {
      try {
        const parsed = JSON.parse(filtersParam);
        // Return parsed filters even if empty (user cleared them)
        return parsed;
      } catch (e) {
        console.error("Error parsing filters from URL:", e);
      }
    }
    // Only set defaults if no filters param exists in URL at all
    return [{ name: "status", value: JSON.stringify(["NEW", "ONGOING"]) }];
  };

  const [controller, setController] = useState({
    filters: getInitialFiltersFromUrl(),
    page: searchParams.get("page") ? parseInt(searchParams.get("page")) : 1,
    per_page: searchParams.get("per_page") ? parseInt(searchParams.get("per_page")) : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "asc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "created_at",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    date_from: searchParams.get("date_from")
      ? searchParams.get("date_from")
      : "",
    date_to: searchParams.get("date_to") ? searchParams.get("date_to") : "",
    fields: searchParams.get("fields")
      ? searchParams.get("fields")
      : "id,report_number,customer.id,customer.name,customer.customer_number,project,delivery_address,status,address,address,postal,place,appointment,report_type",
  });

  const productState = useSelector((store) => {
    return store.reports;
  });

  const [reportExportDialog, setReportExportDialog] = useState(false);
  
  // Track if we've ever had a status filter to prevent resetting to defaults
  const hasHadStatusFilterRef = useRef(false);

  // Update all params from controller including filters
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    
    // Update standard params
    if (controller.date_from) {
      searchParams.set("date_from", controller.date_from);
    } else {
      searchParams.delete("date_from");
    }
    
    if (controller.date_to) {
      searchParams.set("date_to", controller.date_to);
    } else {
      searchParams.delete("date_to");
    }
    
    if (controller.page) {
      searchParams.set("page", controller.page);
    } else {
      searchParams.delete("page");
    }
    
    if (controller.per_page) {
      searchParams.set("per_page", controller.per_page);
    } else {
      searchParams.delete("per_page");
    }
    
    if (controller.query) {
      searchParams.set("query", controller.query);
    } else {
      searchParams.delete("query");
    }
    
    if (controller.sort) {
      searchParams.set("sort", controller.sort);
    } else {
      searchParams.delete("sort");
    }
    
    if (controller.sortBy) {
      searchParams.set("sortBy", controller.sortBy);
    } else {
      searchParams.delete("sortBy");
    }
    
    if (controller.view) {
      searchParams.set("view", controller.view);
    } else {
      searchParams.delete("view");
    }
    
    // Update filters param
    // Only set filters param if there are actual filters
    // Delete it if empty to avoid confusion with empty array string
    if (controller.filters && controller.filters.length > 0) {
      const filtersString = JSON.stringify(controller.filters);
      searchParams.set("filters", filtersString);
    } else {
      // Delete filters param when empty (user cleared all filters)
      searchParams.delete("filters");
    }
    
    // Update URL
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({}, "", newUrl);
  }, [controller]);

  const { isLoading } = productState;
  const [selectedItems, setSelectedItems] = useState([]);

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  // Convert ModuleFilter format to backend format
  const handleFiltersChange = (filters) => {
    const backendFilters = [];

    // Map status filters
    if (filters.statuses && filters.statuses.length > 0) {
      backendFilters.push({
        name: "status",
        value: JSON.stringify(filters.statuses),
      });
      hasHadStatusFilterRef.current = true; // Mark that we've had a status filter
    } else if (filters.statuses && filters.statuses.length === 0) {
      // User explicitly cleared all status filters
      hasHadStatusFilterRef.current = true;
    }

    // Map view filters (from customDropdownStates)
    if (filters.views && filters.views.length > 0) {
      backendFilters.push({
        name: "view",
        value: JSON.stringify(filters.views),
      });
    }

    setController({
      ...controller,
      page: 1,
      query: filters.query || "",
      filters: backendFilters,
    });
  };

  // Initialize ModuleFilter state from controller (memoized)
  const initialFilters = useMemo(() => {
    const initial = {
      query: controller.query,
      statuses: [],
    };

    // Initialize views in customDropdownStates format
    let views = [];
    let hasStatusFilter = false;

    if (controller.filters && controller.filters.length > 0) {
      controller.filters.forEach((filter) => {
        if (filter.name === "status") {
          try {
            const parsedStatuses = JSON.parse(filter.value);
            if (parsedStatuses && parsedStatuses.length > 0) {
              initial.statuses = parsedStatuses;
              hasStatusFilter = true;
            }
          } catch (e) {
            console.error("Error parsing status filter:", e);
          }
        } else if (filter.name === "view") {
          try {
            views = JSON.parse(filter.value);
          } catch (e) {
            console.error("Error parsing view filter:", e);
          }
        }
      });
    }

    // Track if we've had a status filter before
    if (hasStatusFilter) {
      hasHadStatusFilterRef.current = true;
    }
    
    // Only set default statuses on initial mount when no filters param exists in URL at all
    // Don't set defaults if:
    // 1. filters param exists in URL (even if empty - means user cleared filters)
    // 2. controller.filters is an empty array (user explicitly cleared all filters)
    // 3. hasStatusFilter is true (status filter exists, even if empty array)
    // 4. initial.statuses already has values
    // 5. We've ever had a status filter (user has interacted with filters)
    const filtersParam = searchParams.get("filters");
    const hasFiltersParam = filtersParam !== null; // Check if param exists, not if it's truthy
    const userClearedFilters = controller.filters && controller.filters.length === 0;
    
    // Only set defaults if:
    // - No filters param in URL (first load)
    // - User hasn't explicitly cleared filters (controller.filters is not empty array)
    // - No status filter was found
    // - initial.statuses is empty
    // - We've never had a status filter before (first time user sees the page)
    if (!hasFiltersParam && !userClearedFilters && !hasStatusFilter && initial.statuses.length === 0 && !hasHadStatusFilterRef.current) {
      // Only set defaults on initial load when no filters param exists in URL at all
      // and user hasn't explicitly cleared filters and hasn't interacted with filters before
      initial.statuses = ["NEW", "ONGOING"];
    }

    // Add views to customDropdownStates format
    initial.views = views;

    return initial;
  }, [controller.query, controller.filters, searchParams]);

  const debouncedDispatchAction = debounce((controller) => {
    dispatch(getReportsAsync(controller));
  }, 1000);

  useEffect(() => {
    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  // Convert status options to ModuleFilter format
  const statusOptions = reportStatusesOptions.map((option) => ({
    value: option.value,
    label: option.label,
    class: "bg-blue-100 text-blue-800",
  }));

  // Convert view options to ModuleFilter format
  const viewOptions = reportViewsOptions.map((option) => ({
    value: option.value,
    label: option.label,
    class: "bg-purple-100 text-purple-800",
  }));

  const fetchExcel = async () => {
    const payload = {
      ...controller,
      format: "xlsx",
    };
    axiosWithToast(fetchReportExcel(payload));
  };

  const headerActions = [
    {
      component: null,
      label: "Export Excel",
      description: "",
      path: null,
      action: () => setReportExportDialog(true),
      icon: BuildingLibraryIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
  ];

  const [reportEditDialogOpen, setReportEditDialogOpen] = useState(false);
  const [multiEditDialogOpen, setMultiEditDialogOpen] = useState(false);
  const [reportOverviewDialogOpen, setReportOverviewDialogOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={headerActions}
        title={"Reports"}
        meta={[]}
        mainButton={{
          label: "New",
          action: () => setReportEditDialogOpen(true),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <div className="mt-10">
              <ReportActionEditBar
                selectedItems={selectedItems}
                setMultiEditDialogOpen={setMultiEditDialogOpen}
              />
            </div>

            <ModuleFilter
              className={"mt-10"}
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={initialFilters}
              searchPlaceholder="Search reports..."
              filterTitle="Filter Reports"
              acceptedStatuses={reportStatusesOptions.map((opt) => opt.value)}
              customDropdownFilters={[
                {
                  key: "views",
                  label: "Views",
                  options: viewOptions,
                  icon: BuildingLibraryIcon,
                },
              ]}
            />

            <ReportsTable
              onItemsSelect={(items) => setSelectedItems(items)}
              itemState={productState}
              itemsCount={productState.count}
              currentCount={productState.currentCount}
              page={controller.page}
              onPageChange={handlePageChange}
              perPage={controller.per_page}
              onOverviewClick={(report) => {
                setSelectedReportId(report.id);
                setReportOverviewDialogOpen(true);
              }}
            />
          </div>
        </div>
      </DashboardHeader>

      <ReportMultiEditDialog
        selectedItems={selectedItems}
        open={multiEditDialogOpen}
        onClose={() => setMultiEditDialogOpen(false)}
      />

      <ServiceReportEditModal
        open={reportEditDialogOpen}
        onClose={() => setReportEditDialogOpen(false)}
        selected={null}
        // onRefresh={() => getReport()}
      />

      <ReportExportDialog
        open={reportExportDialog}
        isLoading={isLoading}
        controller={controller}
        // onLoading={(state) => setIsLoading(state)}
        onClose={() => setReportExportDialog(false)}
      />

      <ReportOverviewSummary
        open={reportOverviewDialogOpen}
        onClose={() => setReportOverviewDialogOpen(false)}
        reportId={selectedReportId}
        currentUser={null} // List page doesn't have current user context
      />
    </>
  );
};
